import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { Switch } from '../../components/ui/Switch';
import { Spinner } from '../../components/ui/Spinner';
import { Stepper } from '../../components/ui/Stepper';
import { StatTile } from '../../components/ui/StatTile';
import { Toast } from '../../components/ui/Toast';
import { RowMenu } from '../../components/ui/RowMenu';
import { PROVISIONING_VENDORS, vendorById } from '../../config/provisioningVendors';
import { provisioningApi } from '../../lib/provisioningApi';
import { formatIsraelTime, formatIsraelDayTime } from '../../utils/formatters';

// Shared input class, matching the Users page toolbar.
const inputCls = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 dark:border-white/15 dark:bg-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand/40';

const STEPS = ['Details', 'Confirm', 'Result'];
const STEP_INDEX = { details: 0, confirm: 1, running: 1, result: 2 };

/* ---------------------------------------------------------------- icons ---- */

const BoxIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 10v5M9.5 12.5h5" strokeLinecap="round" />
  </svg>
);
const CheckIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 10.5l3.5 3.5L15 6.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CopyIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="7" y="7" width="9" height="9" rx="1.6" />
    <path d="M13 7V5.6A1.6 1.6 0 0 0 11.4 4H5.6A1.6 1.6 0 0 0 4 5.6v5.8A1.6 1.6 0 0 0 5.6 13H7" strokeLinecap="round" />
  </svg>
);
const LinkIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M11 5h4v4M15 5l-6 6M9 5H6a1.5 1.5 0 0 0-1.5 1.5V14A1.5 1.5 0 0 0 6 15.5h7.5A1.5 1.5 0 0 0 15 14v-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrashIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M4 6h12M8 6V4.5A.5.5 0 0 1 8.5 4h3a.5.5 0 0 1 .5.5V6M6 6l.7 9a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L14 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const RerunIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M16 10a6 6 0 1 1-1.8-4.3M16 3.5V7h-3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ------------------------------------------------------------- helpers ---- */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Validate one registry field against a value. Returns an error string or ''.
function validateField(field, raw) {
  const value = String(raw ?? '').trim();
  if (!value) return field.required ? `${field.label} is required.` : '';
  if (field.type === 'email' && !EMAIL_RE.test(value)) return 'Enter a valid email address.';
  if (field.maxLength && value.length > field.maxLength) {
    return `${field.label} must be ${field.maxLength} characters or fewer.`;
  }
  if (field.pattern && !new RegExp(field.pattern).test(value)) {
    return field.patternMessage || `${field.label} contains unsupported characters.`;
  }
  return '';
}

function validateAll(vendor, form) {
  const errors = {};
  (vendor?.fields || []).forEach((f) => {
    const msg = validateField(f, form[f.name]);
    if (msg) errors[f.name] = msg;
  });
  return errors;
}

// Turn a thrown API error into a sentence an operator can act on. The vendor's
// raw string is ALWAYS shown separately — this only adds context on top of it.
function explainError(err) {
  switch (err?.code) {
    case 'duplicate_name':
      return 'A Cove partner with this name already exists. Client names are globally unique across all N-able partners — try a more specific name.';
    case 'auth':
      return "The dashboard's Cove credentials were rejected. Check the server configuration before retrying.";
    case 'timeout':
      return "Cove didn't respond in time. Check the Cove console before retrying — the account may still have been created.";
    case 'not_activated':
      return "The client hasn't activated the account yet — Keeper's invite is still unopened. Nothing is wrong; try again once they've clicked it.";
    case 'window_closed':
      return 'This account has already been activated, so it can no longer be deleted through the API. Removing it now needs a Keeper support ticket.';
    case 'disabled':
      return 'Provisioning is switched off on this server (ENABLE_PROVISIONING is false). Nothing was created.';
    case 'not_configured':
      return "The server has no Cove credentials configured, so it couldn't even attempt this. Nothing was created.";
    default:
      return 'The vendor rejected this request. The exact response is below.';
  }
}

// Refusals raised by OUR server before Cove was contacted at all.
const LOCAL_CODES = ['disabled', 'not_configured', 'window_closed'];

// Only claim nothing happened when that is actually provable.
const createdNothing = (err) => ['duplicate_name', 'auth', 'not_configured', 'disabled', 'not_activated', 'window_closed', 'validation'].includes(err?.code);

/* ---------------------------------------------------------- sub-components -- */

// Statuses that mean "the account EXISTS on the vendor but something still has
// to happen". Cove: the admin user failed. Keeper: the client hasn't activated,
// so billing can't be switched on. Both are amber, not red — nothing is broken.
const INCOMPLETE = { partial: 'Partial', pending_activation: 'Awaiting activation' };

function StatusBadge({ status }) {
  if (status === 'success') return <Badge tone="up">Success</Badge>;
  if (status === 'removed') return <Badge tone="neutral">Removed</Badge>;
  if (INCOMPLETE[status]) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/30">
        {INCOMPLETE[status]}
      </span>
    );
  }
  return <Badge tone="down">Failed</Badge>;
}

// A label/value row with an optional copy button. Used on the result screen.
function CopyField({ label, value, mono, type, tone, secret }) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = () => {
    navigator.clipboard?.writeText(String(value)).then(() => {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const missing = value === null || value === undefined || value === '';
  const valueCls = `min-w-0 break-words text-sm font-medium ${
    // eslint-disable-next-line no-nested-ternary
    missing
      ? (tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500')
      : 'text-navy-800 dark:text-slate-100'
  } ${mono ? 'font-mono' : ''}`;

  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-900/5 py-2 last:border-0 dark:border-white/10">
      <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{label}</span>
      <span className="flex min-w-0 items-center gap-2">
        {missing ? (
          <span className={valueCls}>{tone === 'amber' ? 'Not created' : '—'}</span>
        ) : type === 'link' ? (
          <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline dark:text-brand-light">
            {String(value).replace(/^https?:\/\//, '')}
            <LinkIcon className="h-3.5 w-3.5" />
          </a>
        ) : secret ? (
          <>
            <span className={valueCls}>
              {revealed ? value : '•'.repeat(Math.min(String(value).length, 24))}
            </span>
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand hover:bg-brand/10 dark:text-brand-light"
            >
              {revealed ? 'Hide' : 'Show'}
            </button>
          </>
        ) : (
          <span className={valueCls}>{value}</span>
        )}
        {!missing && type !== 'link' && (
          <button
            type="button"
            onClick={copy}
            aria-label={`Copy ${label}`}
            title={`Copy ${label}`}
            className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-200"
          >
            {copied ? <CheckIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" /> : <CopyIcon className="h-3.5 w-3.5" />}
          </button>
        )}
      </span>
    </div>
  );
}

// Raw vendor response, collapsed. Engineers need the exact string for a ticket.
function RawResponse({ text, open = false, title = 'Vendor response' }) {
  if (!text) return null;
  return (
    <details open={open} className="rounded-xl bg-slate-50 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
        {title}
      </summary>
      <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all border-t border-slate-900/5 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-600 dark:border-white/10 dark:text-slate-300">
        {text}
      </pre>
    </details>
  );
}

function VendorTile({ vendor, onSelect }) {
  const live = vendor.status === 'live';
  return (
    <button
      type="button"
      disabled={!live}
      onClick={() => live && onSelect(vendor.id)}
      className={`group/tile relative flex flex-col items-start gap-2 overflow-hidden rounded-2xl border p-4 text-left transition-all duration-300 ${
        live
          ? 'cursor-pointer border-white/60 bg-white/70 shadow-card ring-1 ring-slate-900/5 backdrop-blur-xl hover:-translate-y-0.5 hover:shadow-card-hover dark:border-white/10 dark:bg-slate-900/60 dark:ring-white/10'
          : 'cursor-not-allowed border-slate-200/70 bg-white/40 opacity-60 dark:border-white/5 dark:bg-slate-900/30'
      }`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: vendor.color }} />
          <span className="font-semibold tracking-tight text-navy-800 dark:text-slate-100">{vendor.label}</span>
        </span>
        {live
          ? <Badge tone="brand">Live</Badge>
          : <Badge tone="neutral">Coming soon</Badge>}
      </div>
      <span className="text-xs text-slate-400 dark:text-slate-500">{vendor.vendorName}</span>
      <span className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">{vendor.description}</span>
      {live && (
        <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-brand dark:text-brand-light">
          Provision
          <svg className="h-4 w-4 transition-transform group-hover/tile:translate-x-0.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M4 10h11M11 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ page ---- */

export default function Provisioning() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Filters
  const [query, setQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rangeFilter, setRangeFilter] = useState('all');

  // Wizard. `vendorId === null` means the modal is closed.
  // NOTE: `form` deliberately lives beside `step`, not inside it — "Back to
  // details" after a failure depends on it surviving step changes.
  const [vendorId, setVendorId] = useState(null);
  const [step, setStep] = useState('details');
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [ack, setAck] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryingRowId, setRetryingRowId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Gate for one-time secrets (N-central's generated password).
  const [savedSecret, setSavedSecret] = useState(false);

  const vendor = vendorId ? vendorById(vendorId) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHistory(await provisioningApi.history());
    } catch (e) {
      setMsg({ type: 'error', title: 'Could not load history', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-dismiss the toast (the Toast component leaves timing to the caller).
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(t);
  }, [msg]);

  // Elapsed-seconds counter for the in-flight step.
  useEffect(() => {
    if (step !== 'running') return undefined;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [step]);

  /* ------------------------------------------------------------- wizard ---- */

  const openWizard = (id, prefill = {}) => {
    const v = vendorById(id);
    // Seed registry defaults first, so `prefill` (a re-run) still wins.
    const defaults = {};
    (v?.fields || []).forEach((f) => { if (f.defaultValue) defaults[f.name] = f.defaultValue; });
    setVendorId(id);
    setStep('details');
    setForm({ ...defaults, ...prefill });
    setErrors({});
    setTouched({});
    setAck(false);
    setResult(null);
    setFailure(null);
    setSavedSecret(false);
    setRetrying(false);
  };

  const closeWizard = () => setVendorId(null);

  // Is an un-acknowledged one-time secret on screen? N-central's generated
  // password can never be re-shown, so dismissing the modal would lose it.
  const secretLocked = step === 'result'
    && !!result
    && !savedSecret
    && (vendor?.result || []).some((f) => f.oneTime && result[f.key]);

  // Block closing while a request is in flight, and while an unsaved one-time
  // secret is displayed — the X, Escape and the backdrop all route through this.
  const modalClose = (step === 'running' || secretLocked) ? () => {} : closeWizard;

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (touched[name]) {
      const field = vendor.fields.find((x) => x.name === name);
      setErrors((e) => ({ ...e, [name]: validateField(field, value) }));
    }
  };

  const blurField = (field) => {
    setTouched((t) => ({ ...t, [field.name]: true }));
    setErrors((e) => ({ ...e, [field.name]: validateField(field, form[field.name]) }));
  };

  const goConfirm = () => {
    const errs = validateAll(vendor, form);
    setErrors(errs);
    setTouched(Object.fromEntries(vendor.fields.map((f) => [f.name, true])));
    if (Object.keys(errs).length) return;
    setAck(false);
    setStep('confirm');
  };

  const submit = async () => {
    setStep('running');
    setResult(null);
    setFailure(null);
    try {
      const res = await provisioningApi.create(vendorId, form);
      setResult(res);
      setStep('result');
      if (res.status === 'success') {
        setMsg({ type: 'success', title: 'Account created', text: `${vendor.shortLabel}: ${res.clientName} (#${res.partnerId})` });
      }
    } catch (e) {
      setFailure(e);
      setStep('result');
    } finally {
      load();
    }
  };

  // Runs the vendor's completing step on the result currently on screen. The
  // account already exists, so this never re-creates it — Cove attaches the
  // missing admin user, Keeper switches billing to paid.
  const completeFromWizard = async () => {
    const inc = vendor?.incomplete;
    if (!result?.id || !inc?.action) return;
    setRetrying(true);
    try {
      const res = await provisioningApi.complete(result.id, inc.action.path);
      setResult(res);
      // A retry issues a fresh one-time secret, so require it to be saved again.
      setSavedSecret(false);
      if (res.status === 'success') {
        setMsg({ type: 'success', title: 'Done', text: `${res.clientName} is now complete.` });
      }
    } catch (e) {
      // For Keeper, 'not_activated' is the expected answer until the client
      // clicks their invite. Show it as information, not a failure.
      setMsg(e.code === 'not_activated'
        ? { type: 'error', title: 'Not activated yet', text: explainError(e) }
        : { type: 'error', title: `${inc.action.label} failed`, text: e.message });
    } finally {
      setRetrying(false);
      load();
    }
  };

  // An incomplete account EXISTS on the vendor but still needs a final step —
  // Cove is missing its admin user, Keeper is waiting on the client to activate
  // before billing can be switched on. Reachable from the history row so closing
  // the wizard is never a dead end.
  const completeRow = async (r) => {
    const v = vendorById(r.vendor);
    const inc = v?.incomplete;
    if (!inc?.action) return;
    setRetryingRowId(r.id);
    try {
      const res = await provisioningApi.complete(r.id, inc.action.path);
      // A retry can mint a one-time secret the vendor will never show again
      // (N-central's password). A toast would lose it, so hand the response to
      // the wizard's result step instead — that view displays it and blocks
      // every exit until it has been acknowledged.
      const carriesSecret = (v.result || []).some((f) => f.oneTime && res[f.key]);
      if (carriesSecret) {
        setVendorId(r.vendor);
        setForm({});
        setErrors({});
        setTouched({});
        setFailure(null);
        setSavedSecret(false);
        setResult(res);
        setStep('result');
        return;
      }
      setMsg(res.status === 'success'
        ? { type: 'success', title: 'Done', text: `${res.clientName} is now complete.` }
        : { type: 'error', title: 'Still incomplete', text: res.errorMessage || inc.heading });
    } catch (e) {
      // 'not_activated' is an expected answer for Keeper, not a failure —
      // say so plainly instead of showing it as an error.
      setMsg(e.code === 'not_activated'
        ? { type: 'error', title: 'Not activated yet', text: explainError(e) }
        : { type: 'error', title: `${inc.action.label} failed`, text: e.message });
    } finally {
      setRetryingRowId(null);
      load();
    }
  };

  // Vendor-side deletion, only offered where the registry says it's possible.
  const removeRow = async (r) => {
    const inc = vendorById(r.vendor)?.incomplete;
    setRetryingRowId(r.id);
    try {
      await provisioningApi.remove(r.id);
      setMsg({ type: 'success', title: 'Removed', text: `${r.clientName} was deleted at the vendor.` });
    } catch (e) {
      setMsg({ type: 'error', title: 'Could not remove', text: explainError(e) || e.message });
    } finally {
      setRetryingRowId(null);
      setConfirmRemove(null);
      load();
      if (inc) { /* keep lint happy about unused when no registry entry */ }
    }
  };

  // All required fields filled and nothing currently invalid.
  const canContinue = useMemo(() => {
    if (!vendor) return false;
    return vendor.fields.every((f) => (!f.required || String(form[f.name] ?? '').trim()))
      && !Object.values(errors).some(Boolean);
  }, [vendor, form, errors]);

  // Free client-side duplicate check. A WARNING, never a blocker — the loaded
  // history is local and the vendor is the real authority on name collisions.
  const duplicateWarning = useMemo(() => {
    const name = String(form.clientName ?? '').trim().toLowerCase();
    if (!name || !vendorId) return null;
    const hit = history.find(
      (r) => r.vendor === vendorId && r.status !== 'failed' && r.clientName.toLowerCase() === name,
    );
    if (!hit) return null;
    return `A ${vendor.shortLabel} account named "${hit.clientName}" was provisioned on ${formatIsraelTime(hit.createdAt)}. Names are globally unique — this will most likely fail.`;
  }, [form.clientName, history, vendorId, vendor]);

  const copySummary = (rowOrResult) => {
    const r = rowOrResult;
    const lines = [
      `Vendor: ${vendorById(r.vendor)?.label || vendor?.label || r.vendor || vendorId}`,
      `Client: ${r.clientName}`,
      r.partnerId ? `Partner ID: ${r.partnerId}` : null,
      `Admin: ${r.adminEmail || 'not created'}`,
      `Status: ${r.status}`,
      r.errorMessage ? `Error: ${r.errorMessage}` : null,
      // Only ever present on a fresh wizard result — history rows never carry a
      // secret, because it is not stored. Driven off the registry so a new
      // vendor with a one-time value is included without touching this.
      ...(vendorById(r.vendor)?.result || [])
        .filter((f) => f.oneTime && r[f.key])
        .map((f) => `${f.label}: ${r[f.key]}`),
      r.consoleUrl ? `Console: ${r.consoleUrl}` : null,
    ].filter(Boolean);
    navigator.clipboard?.writeText(lines.join('\n')).then(
      () => setMsg({ type: 'success', title: 'Copied', text: 'Summary copied to the clipboard.' }),
    ).catch(() => {});
  };

  /* ------------------------------------------------------------ history ---- */

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cutoff = (() => {
      if (rangeFilter === '7d') return Date.now() - 7 * 86400000;
      if (rangeFilter === '30d') return Date.now() - 30 * 86400000;
      if (rangeFilter === 'month') return new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
      return null;
    })();

    return history.filter((r) => {
      if (vendorFilter !== 'all' && r.vendor !== vendorFilter) return false;
      if (statusFilter === 'incomplete') {
        if (!INCOMPLETE[r.status]) return false;
      } else if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (cutoff && new Date(r.createdAt).getTime() < cutoff) return false;
      if (!q) return true;
      return [r.clientName, r.adminEmail, r.partnerId, r.createdBy]
        .some((v) => String(v ?? '').toLowerCase().includes(q));
    });
  }, [history, query, vendorFilter, statusFilter, rangeFilter]);

  const stats = useMemo(() => {
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    return {
      total: history.length,
      thisMonth: history.filter((r) => new Date(r.createdAt).getTime() >= monthStart).length,
      problems: history.filter((r) => r.status !== 'success').length,
      live: PROVISIONING_VENDORS.filter((v) => v.status === 'live').length,
    };
  }, [history]);

  const statusOptions = useMemo(() => {
    const present = new Set(history.map((r) => r.status));
    const anyIncomplete = history.some((r) => INCOMPLETE[r.status]);
    return [
      { value: 'all', label: 'All statuses' },
      ...(present.has('success') ? [{ value: 'success', label: 'Success' }] : []),
      ...(anyIncomplete ? [{ value: 'incomplete', label: 'Needs a final step' }] : []),
      ...(present.has('failed') ? [{ value: 'failed', label: 'Failed' }] : []),
      ...(present.has('removed') ? [{ value: 'removed', label: 'Removed' }] : []),
    ];
  }, [history]);

  const vendorOptions = useMemo(() => {
    const present = new Set(history.map((r) => r.vendor));
    return [
      { value: 'all', label: 'All vendors' },
      ...PROVISIONING_VENDORS.filter((v) => present.has(v.id)).map((v) => ({ value: v.id, label: v.label })),
    ];
  }, [history]);

  // Keep the selected filters valid: if the option a filter points at stops
  // existing (its last record was removed), fall back to 'all' rather than
  // leaving the select on an orphaned value and the table mysteriously empty.
  // NB: must sit AFTER statusOptions/vendorOptions are declared — referencing a
  // `const` above its declaration is a temporal-dead-zone ReferenceError.
  useEffect(() => {
    if (!statusOptions.some((o) => o.value === statusFilter)) setStatusFilter('all');
  }, [statusOptions, statusFilter]);

  useEffect(() => {
    if (!vendorOptions.some((o) => o.value === vendorFilter)) setVendorFilter('all');
  }, [vendorOptions, vendorFilter]);

  const filtersActive = query.trim() !== '' || vendorFilter !== 'all'
    || statusFilter !== 'all' || rangeFilter !== 'all';

  const clearFilters = () => {
    setQuery(''); setVendorFilter('all'); setStatusFilter('all'); setRangeFilter('all');
  };

  const columns = [
    {
      key: 'createdAt',
      label: 'Date',
      render: (_v, r) => {
        const { day, time } = formatIsraelDayTime(r.createdAt);
        return (
          <span className="block whitespace-nowrap leading-tight">
            <span className="block font-medium text-slate-700 dark:text-slate-200">{day}</span>
            <span className="block text-xs tabular-nums text-slate-400 dark:text-slate-500">{time}</span>
          </span>
        );
      },
    },
    {
      key: 'clientName',
      label: 'Client',
      render: (_v, r) => (
        <span className="block min-w-0 max-w-[15rem] leading-tight">
          <span className="block truncate font-medium text-navy-800 dark:text-slate-100">{r.clientName}</span>
          {r.partnerId && (
            <span
              title={r.partnerId}
              className="block truncate font-mono text-xs text-slate-400 dark:text-slate-500"
            >
              {r.partnerId}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (_v, r) => {
        const v = vendorById(r.vendor);
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-600 dark:text-slate-300">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: v?.color || '#94a3b8' }} />
            {v?.shortLabel || r.vendor}
          </span>
        );
      },
    },
    {
      key: 'adminEmail',
      label: 'Admin user',
      render: (_v, r) => (
        <span className="text-slate-500 dark:text-slate-400">{r.adminEmail || '—'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (v, r) => (
        <span className="block leading-tight">
          <StatusBadge status={v} />
          {INCOMPLETE[v] && vendorById(r.vendor)?.incomplete?.rowLabel && (
            <span className="mt-1 block max-w-[11rem] text-xs text-amber-600 dark:text-amber-400">
              {vendorById(r.vendor).incomplete.rowLabel}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'createdBy',
      label: 'By',
      render: (v) => <span className="text-xs text-slate-500 dark:text-slate-400">{v || '—'}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      // The row itself is clickable (expandedContent), so swallow clicks here —
      // otherwise opening the menu also toggles the expanded panel.
      render: (_v, r) => (
        <span
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          className="inline-block"
        >
        <RowMenu
          items={[
            {
              label: 'Copy partner ID',
              icon: <CopyIcon />,
              hidden: !r.partnerId,
              onClick: () => navigator.clipboard?.writeText(r.partnerId).then(
                () => setMsg({ type: 'success', title: 'Copied', text: `Partner ID ${r.partnerId} copied.` }),
              ).catch(() => {}),
            },
            { label: 'Copy summary', icon: <CopyIcon />, onClick: () => copySummary(r) },
            {
              label: 'Open vendor console',
              icon: <LinkIcon />,
              hidden: !vendorById(r.vendor)?.consoleUrl,
              onClick: () => window.open(vendorById(r.vendor).consoleUrl, '_blank', 'noreferrer'),
            },
            {
              label: retryingRowId === r.id
                ? 'Working…'
                : (vendorById(r.vendor)?.incomplete?.action?.label || 'Finish'),
              icon: <RerunIcon />,
              hidden: r.status !== vendorById(r.vendor)?.incomplete?.status
                || vendorById(r.vendor)?.status !== 'live',
              onClick: () => completeRow(r),
            },
            {
              label: vendorById(r.vendor)?.incomplete?.removeLabel || 'Delete at vendor',
              icon: <TrashIcon />,
              danger: true,
              hidden: !vendorById(r.vendor)?.incomplete?.removable
                || r.status !== vendorById(r.vendor)?.incomplete?.status,
              onClick: () => setConfirmRemove(r),
            },
            {
              label: 'Re-run',
              icon: <RerunIcon />,
              hidden: r.status !== 'failed' || vendorById(r.vendor)?.status !== 'live',
              onClick: () => openWizard(r.vendor, { ...r.request }),
            },
          ]}
        />
        </span>
      ),
    },
  ];

  // A small label/value row. Labels sit in a fixed column so the pairs line up
  // and read as a list, instead of label and value drifting to opposite edges.
  const DetailRow = ({ label, children }) => (
    <div className="flex gap-3 py-1 text-sm">
      <dt className="w-32 shrink-0 text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="min-w-0 break-words font-medium text-navy-800 dark:text-slate-100">{children}</dd>
    </div>
  );

  const Section = ({ title, children, className = '' }) => (
    <div className={className}>
      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {title}
      </h4>
      {children}
    </div>
  );

  const expandedContent = (r) => {
    const v = vendorById(r.vendor);
    const req = r.request || {};

    // Show what the operator typed using the registry's human labels, in the
    // same order as the form — the raw keys (`vendorInternalId`, `zipCode`) are
    // implementation detail, not something to read in a table.
    const known = (v?.fields || []).filter((f) => req[f.name] !== undefined && req[f.name] !== '');
    const extra = Object.keys(req).filter((k) => !(v?.fields || []).some((f) => f.name === k));

    return (
      <div className="space-y-5 pt-1">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <Section title="Submitted">
            <dl>
              {known.map((f) => (
                <DetailRow key={f.name} label={f.label}>
                  <span className={f.name === 'vendorInternalId' ? 'font-mono' : undefined}>
                    {String(req[f.name])}
                  </span>
                </DetailRow>
              ))}
              {extra.map((k) => (
                <DetailRow key={k} label={k}>{String(req[k])}</DetailRow>
              ))}
            </dl>
          </Section>

          <Section title="Result">
            <dl>
              <DetailRow label={`${v?.shortLabel || 'Vendor'} ID`}>
                <span className="font-mono">{r.partnerId || '—'}</span>
              </DetailRow>
              <DetailRow label="Admin user">
                {r.adminEmail || <span className="text-slate-400 dark:text-slate-500">Not created</span>}
              </DetailRow>
              <DetailRow label="Status">
                <StatusBadge status={r.status} />
              </DetailRow>
              <DetailRow label="Took">
                {r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s` : '—'}
              </DetailRow>
              <DetailRow label="Created by">{r.createdBy || '—'}</DetailRow>
              {v?.consoleUrl && (
                <DetailRow label="Console">
                  <a
                    href={v.consoleUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand hover:underline dark:text-brand-light"
                  >
                    {v.consoleUrl.replace(/^https?:\/\//, '')}
                  </a>
                </DetailRow>
              )}
            </dl>
          </Section>
        </div>

        {/* Full width: vendor replies are long, and this is the bit that gets
            pasted into a support ticket. No nested disclosure — the heading
            already says what it is. */}
        {r.raw && (
          <Section title="Vendor response">
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-slate-50 p-3 font-mono text-[11px] leading-relaxed text-slate-600 ring-1 ring-slate-900/5 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10">
              {r.raw}
            </pre>
          </Section>
        )}

        {r.errorMessage && r.status !== 'failed' && (
          <Section title="Last error">
            <p className="text-sm text-amber-700 dark:text-amber-400">{r.errorMessage}</p>
          </Section>
        )}
      </div>
    );
  };

  /* --------------------------------------------------------- wizard body ---- */

  const renderField = (field, index) => {
    const value = form[field.name] ?? '';
    const err = touched[field.name] ? errors[field.name] : '';
    const near = field.maxLength >= 20 && String(value).length > field.maxLength * 0.8;
    return (
      <div key={field.name}>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">
            {field.label}
            {field.required
              ? <span className="text-brand"> *</span>
              : (
                <span className="font-normal text-slate-400 dark:text-slate-500">
                  {' '}— optional{field.optionalHint ? `, ${field.optionalHint}` : ''}
                </span>
              )}
          </span>
          <input
            type={field.type === 'email' ? 'email' : 'text'}
            value={value}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            onChange={(e) => setField(field.name, e.target.value)}
            autoFocus={index === 0}
            onBlur={() => {
              // Trim on leaving the field: a stray trailing space is invisible in
              // the input but becomes part of the permanent vendor-side name.
              const trimmed = String(form[field.name] ?? '').trim();
              if (trimmed !== form[field.name]) setForm((f) => ({ ...f, [field.name]: trimmed }));
              blurField(field);
            }}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 ${
              err
                ? 'border-red-400 focus:ring-red-400/40 dark:border-red-500/50'
                : 'border-slate-300 focus:ring-brand/40 dark:border-white/15'
            }`}
          />
        </label>
        <div className="mt-1 flex items-start justify-between gap-3">
          <p className={`text-xs leading-relaxed ${err ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
            {err || field.help}
          </p>
          {near && (
            <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
              {String(value).length}/{field.maxLength}
            </span>
          )}
        </div>
      </div>
    );
  };

  const fixedList = (muted) => (
    <dl className="space-y-1.5">
      {(vendor?.fixed || []).map((f) => (
        <div key={f.label} className="flex justify-between gap-3 text-sm">
          <dt className="text-slate-500 dark:text-slate-400">{f.label}</dt>
          <dd className={muted ? 'text-slate-500 dark:text-slate-400' : 'font-medium text-navy-800 dark:text-slate-100'}>{f.value}</dd>
        </div>
      ))}
    </dl>
  );

  const detailsStep = () => (
    // A real <form> so Enter moves on instead of doing nothing — this screen is
    // two text fields, and reaching for the mouse to continue is needless friction.
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); if (canContinue) goConfirm(); }}
    >
      {vendor.fields.map(renderField)}

      {duplicateWarning && <Alert type="warning">{duplicateWarning}</Alert>}

      <details className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
        <summary className="cursor-pointer select-none text-xs font-semibold text-slate-500 dark:text-slate-400">
          Fixed by server config ({vendor.fixed.length})
        </summary>
        <div className="mt-2 border-t border-slate-900/5 pt-2 dark:border-white/10">{fixedList(true)}</div>
      </details>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={closeWizard}>Cancel</Button>
        <Button type="submit" disabled={!canContinue}>Continue</Button>
      </div>
    </form>
  );

  const confirmStep = () => {
    // The identity field — the one value the vendor will not let us change
    // afterwards. It is what this whole screen exists to have you re-read, so
    // it gets pulled out of the list and shown at full size.
    const primary = vendor.fields[0];
    const primaryValue = String(form[primary?.name] ?? '').trim();
    const rest = vendor.fields.slice(1);

    return (
      <div className="space-y-4">
        <div className="rounded-xl bg-white/60 p-3 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {primary?.label}
            </span>
            <button
              type="button"
              onClick={() => setStep('details')}
              className="rounded text-xs font-semibold text-brand-dark hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:text-brand-light"
            >
              Edit
            </button>
          </div>
          <p className="mt-0.5 break-words text-base font-semibold text-navy-800 dark:text-slate-100">
            {primaryValue}
          </p>

          <dl className="mt-2.5 space-y-1 border-t border-slate-900/5 pt-2.5 dark:border-white/10">
            {rest.map((f) => {
              const entered = String(form[f.name] ?? '').trim();
              return (
                <div key={f.name} className="flex justify-between gap-3 text-sm">
                  <dt className="text-slate-500 dark:text-slate-400">{f.label}</dt>
                  {/* A blank optional field is not nothing — it means a default
                      is about to be applied. Show that, not an empty row. */}
                  <dd className={entered
                    ? 'min-w-0 break-words text-right font-medium text-navy-800 dark:text-slate-100'
                    : 'min-w-0 break-words text-right text-slate-400 dark:text-slate-500'}
                  >
                    {entered || f.optionalHint || '—'}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>

        {/* Collapsed by default: these cannot be changed from here, so on the
            step whose job is "check what you typed" they are pure noise. */}
        <details className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
          <summary className="cursor-pointer select-none text-xs font-semibold text-slate-500 dark:text-slate-400">
            Fixed by server config ({vendor.fixed.length})
          </summary>
          <div className="mt-2 border-t border-slate-900/5 pt-2 dark:border-white/10">{fixedList(true)}</div>
        </details>

        <Switch checked={ack} onChange={setAck} label="I've verified these details." />

        <p className="text-xs leading-relaxed text-slate-400 dark:text-slate-500">
          {vendor.confirmWarning} Cannot be undone from here.
        </p>

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={closeWizard}>Cancel</Button>
          <Button type="button" disabled={!ack} onClick={submit}>Create {vendor.shortLabel} account</Button>
        </div>
      </div>
    );
  };

  const runningStep = () => {
    // Stages are the EXPECTED sequence on a timer, not server-reported progress.
    const stages = [
      { label: 'Validating request', at: 0 },
      { label: `Creating partner account in ${vendor.shortLabel}`, at: 1 },
      { label: 'Creating admin console user', at: 4 },
    ];
    const activeIdx = stages.reduce((acc, s, i) => (elapsed >= s.at ? i : acc), 0);
    return (
      <div className="space-y-5 py-2">
        <Spinner label="Creating account" />
        <ol className="mx-auto max-w-xs space-y-2">
          {stages.map((s, i) => {
            const done = i < activeIdx;
            const active = i === activeIdx;
            return (
              <li key={s.label} className="flex items-center gap-2.5 text-sm">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                  // eslint-disable-next-line no-nested-ternary
                  done
                    ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-300'
                    : active
                      ? 'bg-brand/15 text-brand dark:bg-brand/25 dark:text-brand-light'
                      : 'bg-slate-100 text-slate-300 dark:bg-white/10 dark:text-slate-600'
                }`}
                >
                  {done
                    ? <CheckIcon className="h-3 w-3" />
                    : active
                      ? <span className="h-2 w-2 animate-pulse rounded-full bg-current" />
                      : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                </span>
                <span className={done || active ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'}>
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
        <div className="text-center">
          <p className="text-sm font-medium text-navy-800 dark:text-slate-100">Working… {elapsed}s</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-400 dark:text-slate-500">
            {elapsed > 30
              ? `Still working. ${vendor.vendorName} is slow to respond — the request has not been cancelled.`
              : `This usually takes 5–20 seconds. Don't close this window or refresh — the account is being created on ${vendor.vendorName}'s side.`}
          </p>
        </div>
      </div>
    );
  };

  const resultStep = () => {
    if (failure) {
      return (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300">
              <svg className="h-6 w-6" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round" />
              </svg>
            </span>
            <h4 className="text-base font-semibold text-navy-800 dark:text-slate-100">Provisioning failed</h4>
            <p className="max-w-sm text-sm leading-relaxed text-slate-500 dark:text-slate-400">{explainError(failure)}</p>
          </div>

          <RawResponse
            text={failure.message}
            open
            title={LOCAL_CODES.includes(failure.code) ? 'Server response' : 'Vendor response'}
          />

          {/* The explanation above already states the outcome for local refusals. */}
          {!LOCAL_CODES.includes(failure.code) && (
            <p className="text-center text-xs text-slate-400 dark:text-slate-500">
              {createdNothing(failure)
                ? 'Nothing was created.'
                : `Check the ${vendor.shortLabel} console before retrying — the account may still exist.`}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={closeWizard}>Close</Button>
            <Button type="button" onClick={() => setStep('details')}>Back to details</Button>
          </div>
        </div>
      );
    }

    if (!result) return null;
    const inc = vendor.incomplete;
    // A result field the vendor cannot re-issue (N-central's password).
    const oneTimeField = (vendor.result || []).find((f) => f.oneTime && result[f.key]);
    const locked = !!oneTimeField && !savedSecret;
    const partial = !!inc && result.status === inc.status;

    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className={`flex h-11 w-11 items-center justify-center rounded-full ${
            partial
              ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300'
              : 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-300'
          }`}
          >
            {partial ? (
              <svg className="h-6 w-6" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M10 6.5v4.5M10 13.5h.01" strokeLinecap="round" />
              </svg>
            ) : <CheckIcon className="h-6 w-6" />}
          </span>
          <h4 className="text-base font-semibold text-navy-800 dark:text-slate-100">
            {partial ? inc.heading : `${vendor.shortLabel} account created`}
          </h4>
        </div>

        <div className="rounded-xl bg-white/60 px-3 py-1 ring-1 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10">
          {vendor.result.map((f) => (
            <CopyField
              key={f.key}
              label={f.label}
              value={result[f.key]}
              mono={f.mono}
              type={f.type}
              secret={f.secret}
              tone={partial && f.label === inc.missingLabel ? 'amber' : undefined}
            />
          ))}
        </div>

        {/* Only say an email was sent when one actually was. N-central sends
            nothing, so this line used to state the opposite of the red warning
            directly beneath it. */}
        {partial && <Alert type="warning">{inc.hint}</Alert>}
        {!partial && vendor.sendsAdminEmail && (
          <p className="text-center text-xs text-slate-400 dark:text-slate-500">
            {vendor.vendorName} has emailed {result.adminEmail}.
          </p>
        )}

        {/* A value the vendor will never show again. Make that unmissable and
            block the exits until it has been acknowledged. */}
        {oneTimeField && (
          <Alert type="error">
            <strong className="font-semibold">Copy the {oneTimeField.label.toLowerCase()} now.</strong>{' '}
            {vendor.shortLabel} has no password-reset API, so this is the only way into the
            account. It is not stored anywhere and cannot be shown again.
          </Alert>
        )}

        {oneTimeField && (
          <Switch
            checked={savedSecret}
            onChange={setSavedSecret}
            label={`I've saved the ${oneTimeField.label.toLowerCase()}`}
          />
        )}

        {partial && <RawResponse text={result.raw} />}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => copySummary({ ...result, vendor: vendorId })}>
            Copy all
          </Button>
          <div className="flex flex-wrap gap-2">
            {partial ? (
              <Button type="button" disabled={retrying} onClick={completeFromWizard}>
                {retrying ? 'Working…' : inc.action.label}
              </Button>
            ) : null}
            <Button
              type="button"
              variant={partial ? 'secondary' : 'primary'}
              disabled={locked}
              title={locked ? `Confirm you've saved the ${oneTimeField.label.toLowerCase()} first` : undefined}
              onClick={closeWizard}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------- render ---- */

  return (
    <AppShell title="Provisioning">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatTile label="Total provisioned" value={stats.total} icon={<BoxIcon className="h-5 w-5" />} />
        <StatTile label="This month" value={stats.thisMonth} />
        <StatTile label="Failed / partial" value={stats.problems} tone={stats.problems ? 'amber' : 'green'} />
        <StatTile label="Live vendors" value={`${stats.live} of ${PROVISIONING_VENDORS.length}`} />
      </div>

      <Card title="Select a vendor to provision" subtitle="Creates a real account on the vendor's platform">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PROVISIONING_VENDORS.map((v) => (
            <VendorTile key={v.id} vendor={v} onSelect={openWizard} />
          ))}
        </div>
      </Card>

      <Card title="Provisioning history" subtitle="Accounts created from this dashboard">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="9" r="6" /><path d="M14 14l3 3" strokeLinecap="round" /></svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client, email, partner ID, or operator…"
              className={`${inputCls} w-full pl-9`}
            />
          </div>
          <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} className={inputCls}>
            {vendorOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)} className={inputCls}>
            <option value="all">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="month">This month</option>
          </select>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-medium text-brand hover:underline dark:text-brand-light"
            >
              Clear filters ({filtered.length} of {history.length})
            </button>
          )}
        </div>

        {loading ? (
          <Spinner label="Loading history" />
        ) : (
          <Table
            columns={columns}
            rows={filtered}
            pageSize={10}
            rowKey={(r) => r.id}
            expandedContent={expandedContent}
            empty={history.length
              ? 'No records match these filters.'
              : 'No accounts have been provisioned yet. Pick a vendor above to create one.'}
          />
        )}
      </Card>

      <Modal
        open={!!vendor}
        onClose={modalClose}
        size="xl"
        title={step === 'running' ? `Creating ${vendor?.shortLabel} account…` : `Provision ${vendor?.shortLabel} account`}
      >
        {vendor && (
          <>
            <Stepper steps={STEPS} current={STEP_INDEX[step]} className="mb-5" />
            {step === 'details' && detailsStep()}
            {step === 'confirm' && confirmStep()}
            {step === 'running' && runningStep()}
            {step === 'result' && resultStep()}
          </>
        )}
      </Modal>

      {/* Vendor-side deletion is irreversible AND time-limited — Keeper's window
          closes the moment the client activates — so it gets its own confirm. */}
      <Modal open={!!confirmRemove} onClose={() => setConfirmRemove(null)} title="Delete at the vendor" size="md">
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Permanently delete{' '}
            <strong className="text-navy-800 dark:text-slate-100">{confirmRemove?.clientName}</strong>{' '}
            from {vendorById(confirmRemove?.vendor)?.label}?
          </p>
          {vendorById(confirmRemove?.vendor)?.incomplete?.removeWarning && (
            <Alert type="warning">
              {vendorById(confirmRemove?.vendor).incomplete.removeWarning}
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button
              type="button"
              disabled={retryingRowId === confirmRemove?.id}
              onClick={() => removeRow(confirmRemove)}
              className="!bg-red-600 !text-white !shadow-none hover:!bg-red-700"
            >
              {retryingRowId === confirmRemove?.id ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      <Toast msg={msg} onDismiss={() => setMsg(null)} />
    </AppShell>
  );
}
